import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Mail, RefreshCw, Save, Send, Settings } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { apiErrorDetail, apiFieldErrors } from '@/lib/api/client';
import { emailApi, type EmailSettings, type EmailSettingsUpdate, type EmailTestResult } from '@/lib/api/email';
import toast from 'react-hot-toast';

/** Form state; the port is held as text so the field can be cleared while typing. */
type FormState = Omit<EmailSettingsUpdate, 'smtp_port'> & { smtp_port: string };

const defaults: FormState = {
  enabled: false, smtp_host: '', smtp_port: '587', smtp_use_tls: true, smtp_username: '', smtp_password: '',
  from_address: '', public_url: '',
};

const fieldClass = 'w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff] placeholder-gray-400 dark:placeholder-gray-500';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2';
const hintClass = 'text-xs text-gray-500 dark:text-gray-400 mt-1';

/** Renders a field-level validation message, or nothing when the field is clean. */
function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return <p id={id} className="text-xs text-red-600 dark:text-red-400 mt-1">{message}</p>;
}

/**
 * Admin page for the SMTP settings used to deliver invitation emails.
 *
 * Loads the stored settings from `GET /settings/email`, saves them with
 * `PUT /settings/email` (a blank password keeps the stored one) and can send a
 * test message. The form is withheld entirely when the settings cannot be
 * loaded, so a failed request can never be saved back as a blank configuration;
 * when the stored row cannot be decrypted the page instead asks for the
 * password again before an enabled configuration may be saved.
 */
function EmailSettingsPage() {
  const [form, setForm] = useState<FormState>(defaults);
  const [hasPassword, setHasPassword] = useState(false);
  const [unreadable, setUnreadable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<EmailTestResult | null>(null);

  const applySettings = (settings: EmailSettings) => {
    const { has_password, unreadable: isUnreadable, smtp_port, ...rest } = settings;
    setForm({ ...rest, smtp_port: String(smtp_port), smtp_password: '' });
    setHasPassword(has_password && !isUnreadable);
    setUnreadable(isUnreadable);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      applySettings(await emailApi.get());
      setFieldErrors({});
    } catch (err: unknown) {
      setLoadError(apiErrorDetail(err) || 'The server could not be reached.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => setForm(previous => ({ ...previous, [field]: value }));

  const save = async () => {
    setFieldErrors({});
    const port = Number(form.smtp_port);
    if (form.enabled && (!form.smtp_host.trim() || !form.from_address.trim())) {
      toast.error('SMTP host and from address are required when email is enabled');
      return;
    }
    if (unreadable && form.enabled && !form.smtp_password) {
      toast.error('Enter the SMTP password before saving.');
      return;
    }
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
      toast.error('Port must be a whole number between 1 and 65535.');
      return;
    }
    setSaving(true);
    try {
      applySettings(await emailApi.save({
        enabled: form.enabled,
        smtp_host: form.smtp_host.trim(),
        smtp_port: port,
        smtp_use_tls: form.smtp_use_tls,
        smtp_username: form.smtp_username.trim(),
        smtp_password: form.smtp_password,
        from_address: form.from_address.trim(),
        public_url: form.public_url.trim(),
      }));
      toast.success('Email settings saved');
    } catch (err: unknown) {
      const fields = apiFieldErrors(err);
      setFieldErrors(fields);
      toast.error(apiErrorDetail(err) || Object.values(fields).join('; ') || 'Check the highlighted fields and try again.');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await emailApi.test(testTo.trim()));
    } catch (err: unknown) {
      setTestResult({ sent: false, error: apiErrorDetail(err) || 'Could not reach the server' });
    } finally {
      setTesting(false);
    }
  };

  const passwordHint = unreadable
    ? 'The stored password could not be read — enter it again.'
    : hasPassword
      ? 'A password is stored. Leave blank to keep it.'
      : 'Leave blank if the server needs no authentication.';

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Breadcrumb items={[
          { label: 'Settings', href: '/settings', icon: <Settings className="w-4 h-4" /> },
          { label: 'Email', icon: <Mail className="w-4 h-4" /> },
        ]} />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Email Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">SMTP delivery for invitation emails. Invite links can always be copied, even without email.</p>
        </div>

        {loading && (
          <div role="status" className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">Loading email settings...</div>
        )}

        {!loading && loadError && (
          <div role="alert" className="flex items-start gap-3 rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-sm text-red-800 dark:text-red-300">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="space-y-3">
              <p>Couldn&apos;t load the current email settings. {loadError}</p>
              <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 px-3 py-1.5 border border-red-300 dark:border-red-500/40 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors">
                <RefreshCw className="w-4 h-4" /> Try again
              </button>
            </div>
          </div>
        )}

        {!loading && !loadError && (
          <>
            {unreadable && (
              <div role="alert" className="flex items-start gap-3 rounded-xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-200">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>The stored email settings could not be read (the encryption key may have changed). Re-enter the SMTP password and save.</span>
              </div>
            )}

            <div className="bg-white dark:bg-[#1a1f5c]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">SMTP server</h2>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={form.enabled} onChange={e => update('enabled', e.target.checked)} /> Enable email delivery
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="smtp-host" className={labelClass}>SMTP host</label>
                  <input id="smtp-host" value={form.smtp_host} onChange={e => update('smtp_host', e.target.value)} placeholder="smtp.office365.com" autoComplete="off" spellCheck={false} autoCapitalize="none" aria-invalid={Boolean(fieldErrors.smtp_host)} aria-describedby={fieldErrors.smtp_host ? 'smtp-host-error' : undefined} className={fieldClass} />
                  <FieldError id="smtp-host-error" message={fieldErrors.smtp_host} />
                </div>
                <div>
                  <label htmlFor="smtp-port" className={labelClass}>Port</label>
                  <input id="smtp-port" type="number" min={1} max={65535} value={form.smtp_port} onChange={e => update('smtp_port', e.target.value)} aria-invalid={Boolean(fieldErrors.smtp_port)} aria-describedby={fieldErrors.smtp_port ? 'smtp-port-error' : undefined} className={fieldClass} />
                  <p className={hintClass}>587 uses STARTTLS; 465 uses implicit TLS.</p>
                  <FieldError id="smtp-port-error" message={fieldErrors.smtp_port} />
                </div>
                <div>
                  <label htmlFor="smtp-username" className={labelClass}>Username</label>
                  <input id="smtp-username" value={form.smtp_username} onChange={e => update('smtp_username', e.target.value)} autoComplete="off" spellCheck={false} autoCapitalize="none" aria-invalid={Boolean(fieldErrors.smtp_username)} aria-describedby={fieldErrors.smtp_username ? 'smtp-username-error' : undefined} className={fieldClass} />
                  <FieldError id="smtp-username-error" message={fieldErrors.smtp_username} />
                </div>
                <div>
                  <label htmlFor="smtp-password" className={labelClass}>Password</label>
                  <input id="smtp-password" type="password" value={form.smtp_password} onChange={e => update('smtp_password', e.target.value)} placeholder={hasPassword ? '••••••••' : ''} autoComplete="new-password" aria-invalid={Boolean(fieldErrors.smtp_password)} aria-describedby={fieldErrors.smtp_password ? 'smtp-password-error' : undefined} className={fieldClass} />
                  <p className={hintClass}>{passwordHint}</p>
                  <FieldError id="smtp-password-error" message={fieldErrors.smtp_password} />
                </div>
                <div>
                  <label htmlFor="from-address" className={labelClass}>From address</label>
                  <input id="from-address" type="email" value={form.from_address} onChange={e => update('from_address', e.target.value)} placeholder="noreply@example.com" autoComplete="off" aria-invalid={Boolean(fieldErrors.from_address)} aria-describedby={fieldErrors.from_address ? 'from-address-error' : undefined} className={fieldClass} />
                  <FieldError id="from-address-error" message={fieldErrors.from_address} />
                </div>
                <div>
                  <label htmlFor="public-url" className={labelClass}>Public URL</label>
                  <input id="public-url" value={form.public_url} onChange={e => update('public_url', e.target.value)} placeholder={window.location.origin} autoComplete="off" spellCheck={false} autoCapitalize="none" aria-invalid={Boolean(fieldErrors.public_url)} aria-describedby={fieldErrors.public_url ? 'public-url-error' : undefined} className={fieldClass} />
                  <p className={hintClass}>Base address used in invite links. Leave blank to use the address each request arrives on.</p>
                  <FieldError id="public-url-error" message={fieldErrors.public_url} />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 md:col-span-2">
                  <input type="checkbox" checked={form.smtp_use_tls} onChange={e => update('smtp_use_tls', e.target.checked)} /> Use STARTTLS (recommended for port 587)
                </label>
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={() => void save()} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-[#5869ff] text-white rounded-lg hover:bg-[#4757e6] disabled:opacity-50 transition-colors">
                  <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save settings'}
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-[#1a1f5c]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Test delivery</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Uses the saved settings. Save first if you changed anything above.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label htmlFor="test-to" className={labelClass}>Send test to</label>
                  <input id="test-to" type="email" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@example.com" autoComplete="off" className={fieldClass} />
                </div>
                <div className="sm:self-end">
                  <button type="button" onClick={() => void sendTest()} disabled={testing || !testTo.trim()} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] disabled:opacity-50 transition-colors text-gray-700 dark:text-gray-300">
                    <Send className="w-4 h-4" /> {testing ? 'Sending…' : 'Send test email'}
                  </button>
                </div>
              </div>
              {testResult && (
                <p role="status" className={`text-sm ${testResult.sent ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {testResult.sent ? 'Test email sent.' : `Test failed: ${testResult.error ?? 'Unknown error'}`}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

export default EmailSettingsPage;
